import React, { useEffect } from "react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import type { Project as ProjectT } from "../../api/useProjects";
import { useProject } from "../../api/useProjects";
import usePrevious from "@ui/lib/hooks/usePrevious";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import t from "../../i18n";
import tUI from "@ui/i18n";
import Banner, { useAnimateCloseBanner } from "@ui/Banner";
import useActiveAnalysesStream from "../../api/useActiveAnalysesStream";
import BlankStateV2 from "@ui/BlankStateV2";
import { initMathjsContext } from "diagnostics-lib/runtime";
import { ProjectGlobalSidePanelLayout } from "./GlobalSidePanelLayout";
import { ErrorBoundary } from "react-error-boundary";

const useAnalysisActiveWarningHidden = createGlobalState({
  key: `analysisActiveWarningHidden`,
  defaultValue: false,
  storage: "session",
  type: io.boolean,
  scoped: true,
});

export const ACTIVE_ANALYSIS_COUNT_LIMIT = 3_000;

const ActiveAnalysisWarning = ({ projectId }: { projectId: string }) => {
  const activeAnalysisCount = useActiveAnalysesStream(projectId).activeAnalyses.length;
  const [analysisActiveWarningHidden, setAnalysisActiveWarningHidden] =
    useAnalysisActiveWarningHidden({
      scope: projectId,
    });
  const analysisWarningRef = React.useRef<HTMLDivElement>(null);

  const activeAnalysisCountExceded = activeAnalysisCount > ACTIVE_ANALYSIS_COUNT_LIMIT;
  const showAnalysisActiveWarning = activeAnalysisCountExceded && !analysisActiveWarningHidden;

  const hideAlert = () => {
    setAnalysisActiveWarningHidden(true);
  };
  const handleCloseAnalysisWarning = useAnimateCloseBanner(analysisWarningRef, hideAlert);

  return showAnalysisActiveWarning && activeAnalysisCount !== null ? (
    <Banner uiType="warning" ref={analysisWarningRef} onClose={handleCloseAnalysisWarning}>
      {t("project.analysisActive.count_exceeded", {
        count: activeAnalysisCount,
        countLimit: ACTIVE_ANALYSIS_COUNT_LIMIT,
      })}
    </Banner>
  ) : null;
};

export const ProjectLayout = () => {
  const project = useOutletContext<ProjectT>();

  useEffect(() => {
    async function init() {
      await initMathjsContext();
    }
    void init();
  }, []);

  return (
    <>
      <ErrorBoundary key={project.id} fallbackRender={() => null}>
        <React.Suspense fallback={null}>
          <ActiveAnalysisWarning projectId={project.id} />
        </React.Suspense>
      </ErrorBoundary>
      <ProjectGlobalSidePanelLayout project={project}>
        <Outlet context={project} />
      </ProjectGlobalSidePanelLayout>
    </>
  );
};

export const Project = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  if (!projectId) throw new Error("no project");

  const project = useProject(projectId);
  const previousProject = usePrevious(project);

  if (previousProject && !project && projectId) return <Navigate to="/projects" replace />;

  if (!project)
    return (
      <BlankStateV2
        url={location.pathname}
        subtitle={tUI("404.description")}
        onClick={() => navigate("/")}
        buttonLabel={tUI("404.action")}
      />
    );

  return <Outlet context={project} />;
};
